import { BenchmarkMetric } from "./BenchmarkMetric";
const editLensResults = [
  {
    split: "Standard test",
    description: "Reviews, news, educational web text, and creative writing",
    passages: "6,115",
    accuracy: "89.3%",
    macroF1: "89.1%",
    humanRecall: "92.3%",
    editedRecall: "77.0%",
    generatedRecall: "98.7%",
    humanCleared: "100%",
    editedDetected: "64.11%",
    generatedDetected: "99.36%",
    falsePositives: "0 of 2,038",
  },
  {
    split: "Enron email",
    description: "A separate business-email domain",
    passages: "6,147",
    accuracy: "88.1%",
    macroF1: "88.2%",
    humanRecall: "92.5%",
    editedRecall: "76.2%",
    generatedRecall: "98.2%",
    humanCleared: "100%",
    editedDetected: "56.37%",
    generatedDetected: "99.13%",
    falsePositives: "0 of 1,992",
  },
  {
    split: "Llama",
    description: "AI text produced only with Llama 3.3",
    passages: "5,957",
    accuracy: "92.5%",
    macroF1: "92.3%",
    humanRecall: "92.3%",
    editedRecall: "84.7%",
    generatedRecall: "99.95%",
    humanCleared: "100%",
    editedDetected: "64.27%",
    generatedDetected: "99.71%",
    falsePositives: "0 of 2,038",
  },
];

export function StylomeOneTransparencyReport() {
  return (
    <>
      <section className="content-section">
        <div className="section-heading">
          <div>
            <span className="badge badge-good">5 benchmark reports</span>
            <h2>Strong detection with conservative calls on human writing</h2>
          </div>
          <p>
            Stylome 1.0 is the previous standard model. These July 2026 results
            come from 24,505 public-benchmark passages scored with that
            production model and input contract.
          </p>
        </div>
        <div className="aggregate-counts">
          <p>
            <strong>6,068 of 6,068</strong> human passages were cleared across
            all three EditLens test sections.
          </p>
          <p>
            <strong>5,896 of 5,931</strong> fully generated EditLens passages
            were detected.
          </p>
          <p>
            <strong>2,605 of 2,606</strong> essays were cleared across the
            frozen ELLIPSE and PERSUADE evaluations.
          </p>
        </div>
        <p className="fine evaluation-note">
          Every result below uses the deployed Stylome 1.0 threshold, with no
          threshold fitting on these benchmarks.
        </p>
      </section>

      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">
                Stylome 1.0 · PAN at CLEF 2025
              </span>
              <strong>PAN 2025</strong>
              <span>
                Human and AI-generated essays, fiction, and news from an
                independent detection challenge.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.0 PAN 2025 highlights"
            >
              <BenchmarkMetric
                label="Human writing cleared"
                value="99.61%"
                context="1,272 of 1,277"
              />
              <BenchmarkMetric
                label="AI-generated detected"
                value="94.42%"
                context="2,183 of 2,312"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              The complete labeled validation set, scored at Stylome 1.0’s
              deployed threshold with no threshold fitting on PAN.
            </p>
            <div className="dataset-intro">
              <p>
                <a
                  href="https://pan.webis.de/clef25/pan25-web/generated-content-analysis.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  PAN 2025
                </a>{" "}
                is an academic shared task for distinguishing human writing from
                generated text. Its validation set spans essays, fiction, and
                news, with output from 22 model families and variants.
              </p>
              <p>
                Every one of its 3,589 passages is long enough for Stylome’s
                scanner. None has full-text or 1,000-character-prefix overlap
                with the final Phase 11 training corpus.
              </p>
            </div>
            <div className="aggregate-counts">
              <p>
                <strong>1,272 of 1,277</strong> human passages were correctly
                left unflagged.
              </p>
              <p>
                <strong>2,183 of 2,312</strong> AI-generated passages were
                detected.
              </p>
            </div>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.0 PAN 2025 results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.0 on PAN 2025 by writing type</caption>
                <thead>
                  <tr>
                    <th>Writing type</th>
                    <th>Human cleared</th>
                    <th>AI generated detected</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Essays</th>
                    <td>99.24% (131/132)</td>
                    <td>98.50% (525/533)</td>
                  </tr>
                  <tr>
                    <th scope="row">Fiction</th>
                    <td>99.57% (924/928)</td>
                    <td>92.41% (840/909)</td>
                  </tr>
                  <tr>
                    <th scope="row">News</th>
                    <td>100% (217/217)</td>
                    <td>94.02% (818/870)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluation completed July 2026 on PAN’s labeled validation set,
              not its hidden official test set. Detection on
              Llama-3.1-8B-Instruct was 70.25%; the aggregate results above span
              all 22 model families and variants in the validation set.
            </p>
          </div>
        </details>
      </section>

      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">
                Stylome 1.0 · ELLIPSE Corpus
              </span>
              <strong>ELLIPSE</strong>
              <span>
                Standardized-test essays written by English learners in grades
                8–12.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.0 ELLIPSE highlights"
            >
              <BenchmarkMetric
                label="Human essays cleared"
                value="99.94%"
                context="1,712 of 1,713"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              Only one false positive across a frozen evaluation of 1,713
              learner-English essays.
            </p>
            <div className="dataset-intro">
              <p>
                The open-source{" "}
                <a
                  href="https://doi.org/10.1075/ijlcr.22026.cro"
                  target="_blank"
                  rel="noreferrer"
                >
                  ELLIPSE corpus
                </a>{" "}
                contains roughly 6,500 essays written during statewide testing
                by English learners in grades 8–12.
              </p>
              <p>
                After removing material inherited by an earlier model lineage,
                we split the remaining 3,425 eligible essays with a fixed random
                seed: 1,712 for training and 1,713 for this frozen evaluation.
              </p>
            </div>
            <div className="aggregate-counts">
              <p>
                <strong>1,712 of 1,713</strong> human essays were correctly left
                unflagged.
              </p>
              <p>
                <strong>1 of 1,713</strong> received an incorrect AI-generated
                result.
              </p>
            </div>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.0 ELLIPSE results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.0 on the frozen ELLIPSE half</caption>
                <thead>
                  <tr>
                    <th>Human essays</th>
                    <th>Correctly cleared</th>
                    <th>False positives</th>
                    <th>Clearance rate</th>
                    <th>False-positive rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1,713</td>
                    <td>1,712</td>
                    <td>1</td>
                    <td>99.94%</td>
                    <td>0.06%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluation completed July 2026 at Stylome 1.0’s deployed
              threshold. The held half has zero full-text or prefix overlap with
              final model training. This is a Stylome-defined frozen split, not
              an official detector benchmark split published by the corpus
              authors.
            </p>
          </div>
        </details>
      </section>

      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">Stylome 1.0 · PERSUADE 2.0</span>
              <strong>PERSUADE</strong>
              <span>
                Argumentative essays written by United States students in grades
                6–12.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.0 PERSUADE highlights"
            >
              <BenchmarkMetric
                label="Human essays cleared"
                value="100%"
                context="893 of 893"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              Zero false positives across a frozen evaluation of 893 student
              essays.
            </p>
            <div className="dataset-intro">
              <p>
                The open-source{" "}
                <a
                  href="https://doi.org/10.1016/j.asw.2024.100865"
                  target="_blank"
                  rel="noreferrer"
                >
                  PERSUADE 2.0 corpus
                </a>{" "}
                contains more than 25,000 argumentative essays written by
                students in grades 6–12 across 15 prompts.
              </p>
              <p>
                After removing material inherited by an earlier model lineage,
                we split our 1,785-essay eligible sample with a fixed random
                seed: 892 for training and 893 for this frozen evaluation.
              </p>
            </div>
            <div className="aggregate-counts">
              <p>
                <strong>893 of 893</strong> human essays were correctly left
                unflagged.
              </p>
              <p>
                <strong>0 of 893</strong> received an incorrect AI-generated
                result.
              </p>
            </div>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.0 PERSUADE results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.0 on the frozen PERSUADE half</caption>
                <thead>
                  <tr>
                    <th>Human essays</th>
                    <th>Correctly cleared</th>
                    <th>False positives</th>
                    <th>Clearance rate</th>
                    <th>False-positive rate</th>
                    <th>95% upper bound</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>893</td>
                    <td>893</td>
                    <td>0</td>
                    <td>100%</td>
                    <td>0%</td>
                    <td>0.34%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluation completed July 2026 at Stylome 1.0’s deployed
              threshold. The held half has zero full-text or prefix overlap with
              final model training. With no observed errors, 0.34% is the
              conservative rule-of-three upper bound. This is a Stylome-defined
              frozen split, not an official detector benchmark split published
              by the corpus authors.
            </p>
          </div>
        </details>
      </section>

      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">Stylome 1.0 · Pangram Labs</span>
              <strong>EditLens</strong>
              <span>
                Human-written, AI-edited, and AI-generated text across three
                test sections.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.0 public threshold highlights"
            >
              <BenchmarkMetric
                label="Human writing cleared"
                value="100%"
                context="6,068 of 6,068"
              />
              <BenchmarkMetric
                label="AI-generated detected"
                value="99.41%"
                context="5,896 of 5,931"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              No human false positives across 6,068 passages, while Stylome 1.0
              detected more than 99% of fully generated text in every test
              section.
            </p>
            <div className="dataset-intro">
              <p>
                <a
                  href="https://github.com/pangramlabs/EditLens"
                  target="_blank"
                  rel="noreferrer"
                >
                  EditLens
                </a>{" "}
                is a dataset from Pangram Labs built to study how much AI has
                changed a piece of writing. Unlike a simple human-or-AI test,
                these results use three labels: <strong>human written</strong>,{" "}
                <strong>AI edited</strong>, and <strong>AI generated</strong>.
              </p>
              <p>
                AI-edited writing remains the harder category. Stylome 1.0’s
                strongest three-class section was the Llama split: 92.5% overall
                correct, including 84.7% recall on edited text.
              </p>
            </div>
            <div className="aggregate-counts">
              <p>
                <strong>6,068 of 6,068</strong> human passages were correctly
                left unflagged.
              </p>
              <p>
                <strong>5,896 of 5,931</strong> fully generated passages were
                detected.
              </p>
              <p>
                <strong>3,812 of 6,220</strong> AI-edited passages were detected
                at the same conservative threshold.
              </p>
            </div>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.0 EditLens public threshold results"
              tabIndex={0}
            >
              <table>
                <caption>
                  Performance at Stylome 1.0’s deployed threshold
                </caption>
                <thead>
                  <tr>
                    <th>Test section</th>
                    <th>Human writing cleared</th>
                    <th>False positives</th>
                    <th>AI edited detected</th>
                    <th>AI generated detected</th>
                  </tr>
                </thead>
                <tbody>
                  {editLensResults.map((result) => (
                    <tr key={result.split}>
                      <th scope="row">{result.split}</th>
                      <td>{result.humanCleared}</td>
                      <td>{result.falsePositives}</td>
                      <td>{result.editedDetected}</td>
                      <td>{result.generatedDetected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="benchmark-heading">
              <span className="metadata-line">Research benchmark</span>
              <h3>Three labels make this a harder test</h3>
              <p>
                The live scanner asks whether the evidence is strong enough to
                flag a passage. EditLens also asks the model to distinguish
                AI-edited writing from fully AI-generated writing. A random
                choice among three balanced labels would score about 33%.
              </p>
            </div>
            <div
              className="table-wrap editlens-table"
              role="region"
              aria-label="Stylome 1.0 EditLens three-class results"
              tabIndex={0}
            >
              <table>
                <caption>
                  Three-class performance for Stylome 1.0 on EditLens
                </caption>
                <thead>
                  <tr>
                    <th>Test section</th>
                    <th>Passages</th>
                    <th>Overall correct</th>
                    <th>Balanced score</th>
                    <th>Human found</th>
                    <th>AI edited found</th>
                    <th>AI generated found</th>
                  </tr>
                </thead>
                <tbody>
                  {editLensResults.map((result) => (
                    <tr key={result.split}>
                      <th scope="row">
                        {result.split}
                        <small>{result.description}</small>
                      </th>
                      <td>{result.passages}</td>
                      <td>{result.accuracy}</td>
                      <td>{result.macroF1}</td>
                      <td>{result.humanRecall}</td>
                      <td>{result.editedRecall}</td>
                      <td>{result.generatedRecall}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="metric-explainer">
              <p>
                <strong>Overall correct</strong> is the share assigned the right
                label. <strong>Balanced score</strong> is macro-F1: it gives
                each of the three labels equal weight. The final three columns
                show how often Stylome found each class.
              </p>
              <p>
                The three-way scores complement the deployed-threshold results:
                together they show Stylome 1.0's binary detection strength and
                its separate performance on AI-edited writing across each
                official split.
              </p>
            </div>
            <p className="fine evaluation-note">
              Evaluation completed July 2026 on all three public EditLens test
              sections. The final training audit found zero full-text and zero
              1,000-character-prefix collisions. Official split rows are shown
              so the evaluation remains comparable and reproducible.
            </p>
          </div>
        </details>
      </section>

      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">Stylome 1.0 · Liang et al.</span>
              <strong>TOEFL91</strong>
              <span>
                Human essays written by people learning English as an additional
                language.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.0 TOEFL91 highlights"
            >
              <BenchmarkMetric
                label="Human essays cleared"
                value="96.70%"
                context="88 of 91"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              A deliberately difficult fairness check for false positives on
              human writing by non-native English speakers.
            </p>
            <div className="dataset-intro">
              <p>
                <a
                  href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10382961/"
                  target="_blank"
                  rel="noreferrer"
                >
                  TOEFL91
                </a>{" "}
                contains 91 human-written TOEFL essays used by Liang and
                colleagues to study detector bias against non-native English
                writers.
              </p>
              <p>
                Their 2023 study reported that seven contemporary detectors
                falsely flagged an average of 61.3% of these essays. Stylome 1.0
                cleared 96.70% of the same essays, reducing the false-positive
                rate to 3.30%.
              </p>
            </div>
            <div className="aggregate-counts">
              <p>
                <strong>88 of 91</strong> human essays were correctly left
                unflagged.
              </p>
              <p>
                <strong>3 of 91</strong> received an incorrect AI-generated
                result.
              </p>
            </div>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.0 TOEFL91 results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.0 on TOEFL91</caption>
                <thead>
                  <tr>
                    <th>Human essays</th>
                    <th>Correctly cleared</th>
                    <th>False positives</th>
                    <th>False-positive rate</th>
                    <th>95% interval</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>91</td>
                    <td>88</td>
                    <td>3</td>
                    <td>3.30%</td>
                    <td>0.69–9.33%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluation completed July 2026 at Stylome 1.0’s deployed
              threshold. The essays have zero full-text or prefix overlap with
              final model training. This small, single-register sample should
              not be read as a complete measure of performance for multilingual
              or non-native English writing.
            </p>
          </div>
        </details>
      </section>
    </>
  );
}
